export interface EditorJsBlock<Data = any> {
  id?: string;
  type: string;
  data: Data;
  /** Editor.js block tune payloads (e.g. `aiInstructions` string). */
  tunes?: Record<string, unknown>;
}

export interface EditorJsData {
  time?: number;
  blocks: EditorJsBlock[];
  version?: string;
}

export type SubpagesBlockData = {
  /**
   * Optional visual style for the subpages block.
   * This is intentionally minimal so existing documents remain compatible.
   */
  style?: 'list' | 'compact';
};

export type GalleryBlockItem = {
  url: string;
  caption?: string;
};

export type GalleryBlockData = {
  /** Number of images per row in the grid. Defaults to 3 when missing or invalid. */
  columnCount?: number;
  items: GalleryBlockItem[];
};

export const DEFAULT_GALLERY_COLUMN_COUNT = 3;
export const MAX_GALLERY_COLUMN_COUNT = 12;

function normalizeGalleryColumnCount(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return DEFAULT_GALLERY_COLUMN_COUNT;
  }
  const n = Math.floor(raw);
  if (n < 1) return DEFAULT_GALLERY_COLUMN_COUNT;
  if (n > MAX_GALLERY_COLUMN_COUNT) return MAX_GALLERY_COLUMN_COUNT;
  return n;
}

function normalizeGalleryItem(raw: unknown): GalleryBlockItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const url = rec.url;
  if (typeof url !== 'string' || !url.trim()) return null;
  const caption = rec.caption;
  const item: GalleryBlockItem = { url: url.trim() };
  if (typeof caption === 'string' && caption.trim()) {
    item.caption = caption.trim();
  }
  return item;
}

/**
 * Coerces unknown JSON/editor state into a stable gallery block payload.
 */
export function normalizeGalleryBlockData(input: unknown): GalleryBlockData {
  if (!input || typeof input !== 'object') {
    return { columnCount: DEFAULT_GALLERY_COLUMN_COUNT, items: [] };
  }
  const rec = input as Record<string, unknown>;
  const itemsRaw = rec.items;
  const items: GalleryBlockItem[] = [];
  if (Array.isArray(itemsRaw)) {
    for (const el of itemsRaw) {
      const item = normalizeGalleryItem(el);
      if (item) items.push(item);
    }
  }
  return {
    columnCount: normalizeGalleryColumnCount(rec.columnCount),
    items,
  };
}

export function parseEditorJs(
  value: string | null | undefined,
): EditorJsData | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as EditorJsData;
    if (!parsed || !Array.isArray(parsed.blocks)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function stringifyEditorJs(
  data: EditorJsData | null | undefined,
): string {
  if (!data) {
    return JSON.stringify({ time: Date.now(), blocks: [], version: '2.0.0' });
  }
  return JSON.stringify(data);
}
